import { useCallback, useEffect, useState } from "react";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/modal";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { handleApiError } from "@/services/api";
import { todayISO } from "@/lib/utils";
import { useIsViewer } from "@/hooks/useRole";
import {
  vaccinationService,
  routeLabel,
  timingLabel,
  STATUS_LABEL,
  STATUS_TONE,
  type VaccinationPlan,
  type VaccinationPlanRow,
} from "@/services/vaccinationService";

function formatDate(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** A row's identity. Programme rows are keyed on the entry, ad-hoc on the dose. */
const rowKey = (r: VaccinationPlanRow) =>
  r.schedule_id != null ? `s${r.schedule_id}` : `d${r.dose_id}`;

function CountChip({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: string;
}) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${tone}`}>
      {count} {label}
    </span>
  );
}

export default function FlockVaccinationCard({ flockId }: { flockId: number }) {
  const isViewer = useIsViewer();

  const [plan, setPlan] = useState<VaccinationPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [modalRow, setModalRow] = useState<VaccinationPlanRow | null>(null);
  // Separate from modalRow so an ad-hoc dose (no programme row) can open the
  // same form. null row + open = off-programme.
  const [modalOpen, setModalOpen] = useState(false);
  const [givenOn, setGivenOn] = useState(todayISO());
  const [vaccine, setVaccine] = useState("");
  const [batch, setBatch] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");
  const [removing, setRemoving] = useState<number | null>(null);

  const removeDose = async (row: VaccinationPlanRow) => {
    if (row.dose_id == null) return;
    if (
      !window.confirm(
        `Remove the ${row.vaccine} dose recorded on ${formatDate(row.given_on)}? ` +
          `The programme entry stays; only this record of it being given is deleted.`,
      )
    ) {
      return;
    }
    setRemoving(row.dose_id);
    setError("");
    try {
      await vaccinationService.deleteDose(row.dose_id);
      await load();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setRemoving(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPlan(await vaccinationService.getFlockPlan(flockId));
      setError("");
    } catch (err) {
      setPlan(null);
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [flockId]);

  useEffect(() => {
    load();
  }, [load]);

  const openRecord = (row: VaccinationPlanRow | null) => {
    setModalRow(row);
    setGivenOn(todayISO());
    setVaccine(row?.vaccine ?? "");
    setBatch("");
    setNotes("");
    setModalError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setModalError("");
    try {
      await vaccinationService.recordDose({
        flock_id: flockId,
        // Present for a programme row, absent for an off-programme dose. The
        // server takes the vaccine name off the entry when this is set, so the
        // two can never disagree.
        schedule_id: modalRow?.schedule_id ?? null,
        vaccine: modalRow ? undefined : vaccine.trim(),
        given_on: givenOn,
        batch: batch.trim() === "" ? null : batch.trim(),
        notes: notes.trim() === "" ? null : notes.trim(),
      });
      setModalOpen(false);
      await load();
    } catch (err) {
      setModalError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mb-6 card p-5 sm:p-6 text-sm text-gray-500 dark:text-gray-400">
        Loading vaccination plan…
      </div>
    );
  }

  return (
    <>
      <div className="mb-6 card p-5 sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
              Vaccination
            </h3>
            {plan && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {plan.age_days} days old
              </p>
            )}
          </div>
          {!isViewer && (
            <Button size="sm" variant="outline" onClick={() => openRecord(null)}>
              Record other dose
            </Button>
          )}
        </div>

        {error ? (
          <p
            role="alert"
            className="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400"
          >
            {error}
          </p>
        ) : !plan || plan.rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No vaccination programme set up yet. Add one under Master Data →
            Vaccination programme.
          </p>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap gap-2">
              {plan.overdue > 0 && (
                <CountChip label="overdue" count={plan.overdue} tone={STATUS_TONE.overdue} />
              )}
              {plan.due > 0 && <CountChip label="due" count={plan.due} tone={STATUS_TONE.due} />}
              <CountChip label="given" count={plan.done} tone={STATUS_TONE.done} />
              <CountChip label="upcoming" count={plan.upcoming} tone={STATUS_TONE.upcoming} />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium text-gray-500 dark:border-gray-800 dark:text-gray-400">
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Vaccine</th>
                    <th className="px-3 py-2">Due</th>
                    <th className="px-3 py-2">Date given</th>
                    <th className="px-3 py-2">Route</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {plan.rows.map((row) => (
                    <tr key={rowKey(row)}>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[row.status]}`}
                        >
                          {STATUS_LABEL[row.status]}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-800 dark:text-white/90">
                        {row.vaccine}
                        {row.schedule_id == null && (
                          <span className="ml-2 text-xs font-normal text-gray-400">
                            off programme
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                        {formatDate(row.due_date)}
                        {row.day_of_age != null && (
                          <span className="ml-2 text-xs text-gray-400">
                            day {row.day_of_age}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                        {formatDate(row.given_on)}
                        <span className="ml-2 text-xs text-gray-400">{timingLabel(row)}</span>
                      </td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                        {row.route ? routeLabel(row.route) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {!isViewer && row.dose_id == null && (
                          <button
                            type="button"
                            onClick={() => openRecord(row)}
                            className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                          >
                            Record dose
                          </button>
                        )}
                        {/* A dose recorded by mistake — wrong date, wrong
                            flock, wrong row — was previously unfixable from the
                            UI: the Record button disappears once a dose exists
                            and the unique index blocks a second one. For a
                            record of what went into the birds, "it's a database
                            fix" is not an acceptable answer. */}
                        {!isViewer && row.dose_id != null && (
                          <button
                            type="button"
                            onClick={() => removeDose(row)}
                            disabled={removing === row.dose_id}
                            className="text-sm text-error-600 hover:text-error-700 disabled:opacity-50 dark:text-error-400"
                          >
                            {removing === row.dose_id ? "Removing…" : "Undo"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        className="mx-4 max-w-md p-5 sm:p-8"
      >
        <h4 className="mb-1 text-lg font-semibold text-gray-800 dark:text-white/90">
          Record dose
        </h4>
        <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
          {modalRow
            ? `${modalRow.vaccine} · due ${formatDate(modalRow.due_date)}`
            : "A vaccine given outside the programme."}
        </p>

        {modalError && (
          <div
            role="alert"
            className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500"
          >
            {modalError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!modalRow && (
            <div>
              <Label htmlFor="dose-vaccine">Vaccine *</Label>
              <Input
                id="dose-vaccine"
                value={vaccine}
                onChange={(e) => setVaccine(e.target.value)}
                required
                maxLength={120}
              />
            </div>
          )}

          <div>
            <Label htmlFor="dose-given-on">Date given *</Label>
            <Input
              id="dose-given-on"
              type="date"
              value={givenOn}
              max={todayISO()}
              onChange={(e) => setGivenOn(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="dose-batch">Vaccine batch</Label>
            <Input
              id="dose-batch"
              value={batch}
              onChange={(e) => setBatch(e.target.value)}
              maxLength={80}
            />
          </div>

          <div>
            <Label htmlFor="dose-notes">Notes</Label>
            <Input
              id="dose-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={saving || !givenOn || (!modalRow && !vaccine.trim())}
            >
              {saving ? "Saving…" : "Record"}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
